import { gql } from '@apollo/client';

export const ALL_DRAFT_QUOTES_QUERY = gql`
  query AllDraftQuotes($domainId: Int!) {
    allDraftQuotes(condition: { domainId: $domainId }, orderBy: CREATED_AT_DESC) {
      nodes {
        id
        name
        createdAt
        finalizedQuoteId
        jobByJobId {
          id
          name
          customerByCustomerId {
            id
            name
          }
        }
      }
    }
  }
`;

// Powers the Blueprint generator's ?draftQuoteObjectId=... deep link
// (NewBlueprintSession.tsx) — a Work Order furniture line item links here so
// its already-collected build spec pre-fills the Blueprint form instead of
// being retyped by hand. PostGraphile auto-generates this <table>ById query
// for every table with a primary key, same convention as workOrderById/
// quoteById used elsewhere in this codebase.
export const DRAFT_QUOTE_OBJECT_BY_ID_QUERY = gql`
  query DraftQuoteObjectById($id: Int!) {
    draftQuoteObjectById(id: $id) {
      id
      name
      quantity
      itemType
      furnitureType
      widthInches
      depthInches
      heightInches
      seatHeightInches
      seatDepthInches
      armHeightInches
      backHeightInches
      legHeightInches
      cushionThicknessInches
      armThicknessInches
      seatDeckThicknessInches
      backCushionThicknessInches
      legProfile
      fabricDescription
    }
  }
`;

// Powers the Blueprints viewer's "add to work order" buttons
// (Blueprints.tsx) — looked up fresh when the session loads (rather than
// trusting whatever work order list NewBlueprintSession.tsx saw at prefill
// time) since a work order could be created/removed between selecting the
// object and saving an image against it. A draft_quote_object can back line
// items on more than one work order (work_order_line_item.draft_quote_object_id
// is a plain FK with no uniqueness constraint — see CLAUDE.md's Roller Shade
// section), so this returns every one, not just the first.
export const DRAFT_QUOTE_OBJECT_WORK_ORDERS_QUERY = gql`
  query DraftQuoteObjectWorkOrders($id: Int!) {
    draftQuoteObjectById(id: $id) {
      id
      name
      workOrderLineItemsByDraftQuoteObjectId {
        nodes {
          workOrderByWorkOrderId {
            id
            travelerNumber
          }
        }
      }
    }
  }
`;

// Powers Blueprints.tsx's "attach to a work order" picker for a session
// started from scratch (no ?draftQuoteObjectId= deep link, no browse-table
// pick — see NewBlueprintSession.tsx). Resolves the work order's own
// furniture line item back to its draft_quote_object_id so the existing
// draftQuoteObjectId-based attach flow (ATTACH_BLUEPRINT_IMAGES_TO_WORK_ORDERS_MUTATION)
// can be reused unchanged — a work order with no furniture line item has
// nothing valid to resolve to (draft_quote_object_image requires an owning
// draft_quote_object; see attachBlueprintImagesToWorkOrdersPlugin.ts).
export const WORK_ORDER_FURNITURE_OBJECT_QUERY = gql`
  query WorkOrderFurnitureObject($id: Int!) {
    workOrderById(id: $id) {
      id
      description
      workOrderLineItemsByWorkOrderId {
        nodes {
          draftQuoteObjectId
          draftQuoteObjectByDraftQuoteObjectId {
            id
            itemType
          }
        }
      }
    }
  }
`;

// Saves one or more blueprint canvas captures as real draft_quote_object_image
// rows and links each onto every work order currently referencing that
// draft object (see attachBlueprintImagesToWorkOrdersPlugin.ts server-side).
export const ATTACH_BLUEPRINT_IMAGES_TO_WORK_ORDERS_MUTATION = gql`
  mutation AttachBlueprintImagesToWorkOrders(
    $draftQuoteObjectId: Int!
    $images: [BlueprintImageInput!]!
  ) {
    attachBlueprintImagesToWorkOrders(
      draftQuoteObjectId: $draftQuoteObjectId
      images: $images
    ) {
      imageIds
      workOrderIds
    }
  }
`;

export const CREATE_DRAFT_QUOTE_MUTATION = gql`
  mutation CreateDraftQuote(
    $domainId: Int!
    $jobId: Int!
    $name: String!
    $billToAddressId: Int
    $shipToAddressId: Int
    $notes: String
    $createdBy: Int!
  ) {
    createDraftQuote(
      input: {
        draftQuote: {
          domainId: $domainId
          jobId: $jobId
          name: $name
          billToAddressId: $billToAddressId
          shipToAddressId: $shipToAddressId
          notes: $notes
          createdBy: $createdBy
        }
      }
    ) {
      draftQuote {
        id
        name
      }
    }
  }
`;

export const UPDATE_DRAFT_QUOTE_MUTATION = gql`
  mutation UpdateDraftQuote($id: Int!, $billToAddressId: Int, $shipToAddressId: Int, $notes: String) {
    updateDraftQuoteById(
      input: {
        id: $id
        draftQuotePatch: { billToAddressId: $billToAddressId, shipToAddressId: $shipToAddressId, notes: $notes }
      }
    ) {
      draftQuote {
        id
      }
    }
  }
`;

// Surfaces the flat, once-per-quote charges (Measure Charge, Remote) that
// finalizeQuote will append on top of the draft's own object line items —
// see finalizeQuotePlugin.ts's computeFinalizationExtras, the single source
// of truth this query reads from (not reimplemented client-side).
export const DRAFT_QUOTE_FINALIZATION_PREVIEW_QUERY = gql`
  query DraftQuoteFinalizationPreview($draftQuoteId: Int!) {
    draftQuoteFinalizationPreview(draftQuoteId: $draftQuoteId) {
      measureChargeApplies
      measureChargeCents
      motorCount
      remoteApplies
      remoteCents
      remoteDescription
    }
  }
`;

export const DRAFT_QUOTE_BY_ID_QUERY = gql`
  query DraftQuoteById($id: Int!) {
    draftQuoteById(id: $id) {
      id
      name
      notes
      createdAt
      finalizedQuoteId
      jobByJobId {
        id
        name
        addressByAddressId {
          id
          street
          city
          state
          zip
        }
        customerByCustomerId {
          id
          name
          email
          phone
          addressesByCustomerId {
            nodes {
              id
              street
              city
              state
              zip
            }
          }
        }
      }
      addressByBillToAddressId {
        id
        street
        city
        state
        zip
      }
      addressByShipToAddressId {
        id
        street
        city
        state
        zip
      }
      draftQuoteObjectsByDraftQuoteId(orderBy: SORT_ORDER_ASC) {
        nodes {
          id
          name
          sortOrder
          quantity
          widthInches
          heightInches
          fabricDescription
          liningType
          bumpInterlining
          operatingSystem
          hardwareType
          itemType
          mountType
          rollDirection
          driveSide
          chainColor
          depthInches
          bedSize
          weltType
          insertType
          dustskirtStyle
          spaceAboveWindowInches
          trimPlacement
          fabricWidthInches
          liningWidthInches
          flapSizeInches
          patternRepeatInches
          liningDescription
          fasciaSize
          mechanism
          inventoryItemId
          interliningType
          fabricRepeatInches
          rodFaceInches
          overlapInches
          returnInches
          hemSize
          pleatType
          ruffle
          shamSize
          dropInches
          duvetInsert
          trimDescription
          seatHeightInches
          seatDepthInches
          armHeightInches
          backHeightInches
          legHeightInches
          cushionThicknessInches
          armThicknessInches
          seatDeckThicknessInches
          backCushionThicknessInches
          legProfile
          furnitureType
          contrastWeltFabricDescription
          customWoodFascia
          customWoodFasciaPaintColor
          benchType
          benchShape
          benchStyle
          storageOption
          channelSize
          pillowTopSize
          legSize
          legStyle
          legFinish
          legPriceCents
          trimTop
          trimBottom
          nailheadTop
          nailheadBottom
          nailheadColor
          corniceShape
          corniceOverProduct
          nailheadSides
          nailheadFace
          weltTop
          weltBottom
          weltSides
          weltFace
          bedConfig
          headboardStyle
          headboardShaped
          bedEdgeTreatment
          trimSides
          trimFace
          mattressLengthInches
          mattressWidthInches
          frameThicknessInches
          sideRailFootboardHeightInches
          sideRailFootboardStyleDescription
          bedDrawersSmallQty
          bedDrawersLargeQty
          bedTrundle
          bedFeetType
          bedFeetHeightInches
          bedFeetFinish
          bedFeetSpecialtyDescription
          bedFeetSpecialtyPriceCents
          bedBunkyBoards
          bedBunkyBoardWidthInches
          includeMeasureCharge
          notes
          archivedAt
          draftQuoteObjectItemsByDraftQuoteObjectId(orderBy: ID_ASC) {
            nodes {
              id
              section
              productType
              description
              quantity
              unitPrice
              notes
              formulaNote
              archivedAt
            }
          }
          draftQuoteObjectImagesByDraftQuoteObjectId(orderBy: CREATED_AT_ASC) {
            nodes {
              id
              storedFilename
              originalFilename
              createdAt
              archivedAt
            }
          }
        }
      }
    }
  }
`;

// Calls the custom addDraftQuoteObject plugin which creates the object
// and auto-generates section-tagged items from SOP calculations.
export const ADD_DRAFT_QUOTE_OBJECT_MUTATION = gql`
  mutation AddDraftQuoteObject(
    $draftQuoteId: Int!
    $name: String
    $sortOrder: Int!
    $quantity: Int
    $itemType: String
    $widthInches: Float
    $heightInches: Float
    $depthInches: Float
    $fabricDescription: String
    $liningType: String
    $bumpInterlining: Boolean
    $operatingSystem: String
    $hardwareType: String
    $mountType: String
    $rollDirection: String
    $driveSide: String
    $chainColor: String
    $bedSize: String
    $weltType: String
    $insertType: String
    $dustskirtStyle: String
    $spaceAboveWindowInches: Float
    $trimPlacement: String
    $fabricWidthInches: Float
    $liningWidthInches: Float
    $flapSizeInches: Float
    $patternRepeatInches: Float
    $liningDescription: String
    $fasciaSize: Int
    $mechanism: String
    $inventoryItemId: Int
    $interliningType: String
    $fabricRepeatInches: Float
    $rodFaceInches: Float
    $overlapInches: Float
    $returnInches: Float
    $hemSize: String
    $pleatType: String
    $ruffle: Boolean
    $shamSize: String
    $dropInches: Float
    $trimDescription: String
    $seatHeightInches: Float
    $seatDepthInches: Float
    $armHeightInches: Float
    $backHeightInches: Float
    $legHeightInches: Float
    $cushionThicknessInches: Float
    $armThicknessInches: Float
    $seatDeckThicknessInches: Float
    $backCushionThicknessInches: Float
    $legProfile: String
    $furnitureType: String
    $contrastWeltFabricDescription: String
    $customWoodFascia: Boolean
    $customWoodFasciaPaintColor: String
    $notes: String
  ) {
    addDraftQuoteObject(
      draftQuoteId: $draftQuoteId
      name: $name
      sortOrder: $sortOrder
      quantity: $quantity
      itemType: $itemType
      widthInches: $widthInches
      heightInches: $heightInches
      depthInches: $depthInches
      fabricDescription: $fabricDescription
      liningType: $liningType
      bumpInterlining: $bumpInterlining
      operatingSystem: $operatingSystem
      hardwareType: $hardwareType
      mountType: $mountType
      rollDirection: $rollDirection
      driveSide: $driveSide
      chainColor: $chainColor
      bedSize: $bedSize
      weltType: $weltType
      insertType: $insertType
      dustskirtStyle: $dustskirtStyle
      spaceAboveWindowInches: $spaceAboveWindowInches
      trimPlacement: $trimPlacement
      fabricWidthInches: $fabricWidthInches
      liningWidthInches: $liningWidthInches
      flapSizeInches: $flapSizeInches
      patternRepeatInches: $patternRepeatInches
      liningDescription: $liningDescription
      fasciaSize: $fasciaSize
      mechanism: $mechanism
      inventoryItemId: $inventoryItemId
      interliningType: $interliningType
      fabricRepeatInches: $fabricRepeatInches
      rodFaceInches: $rodFaceInches
      overlapInches: $overlapInches
      returnInches: $returnInches
      hemSize: $hemSize
      pleatType: $pleatType
      ruffle: $ruffle
      shamSize: $shamSize
      dropInches: $dropInches
      trimDescription: $trimDescription
      seatHeightInches: $seatHeightInches
      seatDepthInches: $seatDepthInches
      armHeightInches: $armHeightInches
      backHeightInches: $backHeightInches
      legHeightInches: $legHeightInches
      cushionThicknessInches: $cushionThicknessInches
      armThicknessInches: $armThicknessInches
      seatDeckThicknessInches: $seatDeckThicknessInches
      backCushionThicknessInches: $backCushionThicknessInches
      legProfile: $legProfile
      furnitureType: $furnitureType
      contrastWeltFabricDescription: $contrastWeltFabricDescription
      customWoodFascia: $customWoodFascia
      customWoodFasciaPaintColor: $customWoodFasciaPaintColor
      notes: $notes
    ) {
      objectId
    }
  }
`;

// Calls the custom updateDraftQuoteObject plugin — the "go back and edit"
// flow: updates an existing object's own columns and replaces its
// SOP-calculated items (delete + recompute), mirroring addDraftQuoteObject's
// resolution but scoped to one already-saved object.
export const UPDATE_DRAFT_QUOTE_OBJECT_MUTATION = gql`
  mutation UpdateDraftQuoteObject(
    $objectId: Int!
    $draftQuoteId: Int!
    $name: String
    $quantity: Int
    $itemType: String
    $widthInches: Float
    $heightInches: Float
    $depthInches: Float
    $fabricDescription: String
    $liningType: String
    $bumpInterlining: Boolean
    $operatingSystem: String
    $hardwareType: String
    $mountType: String
    $rollDirection: String
    $driveSide: String
    $chainColor: String
    $bedSize: String
    $weltType: String
    $insertType: String
    $dustskirtStyle: String
    $spaceAboveWindowInches: Float
    $trimPlacement: String
    $fabricWidthInches: Float
    $liningWidthInches: Float
    $flapSizeInches: Float
    $patternRepeatInches: Float
    $liningDescription: String
    $fasciaSize: Int
    $mechanism: String
    $inventoryItemId: Int
    $interliningType: String
    $fabricRepeatInches: Float
    $rodFaceInches: Float
    $overlapInches: Float
    $returnInches: Float
    $hemSize: String
    $pleatType: String
    $ruffle: Boolean
    $shamSize: String
    $dropInches: Float
    $trimDescription: String
    $seatHeightInches: Float
    $seatDepthInches: Float
    $armHeightInches: Float
    $backHeightInches: Float
    $legHeightInches: Float
    $cushionThicknessInches: Float
    $armThicknessInches: Float
    $seatDeckThicknessInches: Float
    $backCushionThicknessInches: Float
    $legProfile: String
    $furnitureType: String
    $contrastWeltFabricDescription: String
    $customWoodFascia: Boolean
    $customWoodFasciaPaintColor: String
    $notes: String
  ) {
    updateDraftQuoteObject(
      objectId: $objectId
      draftQuoteId: $draftQuoteId
      name: $name
      quantity: $quantity
      itemType: $itemType
      widthInches: $widthInches
      heightInches: $heightInches
      depthInches: $depthInches
      fabricDescription: $fabricDescription
      liningType: $liningType
      bumpInterlining: $bumpInterlining
      operatingSystem: $operatingSystem
      hardwareType: $hardwareType
      mountType: $mountType
      rollDirection: $rollDirection
      driveSide: $driveSide
      chainColor: $chainColor
      bedSize: $bedSize
      weltType: $weltType
      insertType: $insertType
      dustskirtStyle: $dustskirtStyle
      spaceAboveWindowInches: $spaceAboveWindowInches
      trimPlacement: $trimPlacement
      fabricWidthInches: $fabricWidthInches
      liningWidthInches: $liningWidthInches
      flapSizeInches: $flapSizeInches
      patternRepeatInches: $patternRepeatInches
      liningDescription: $liningDescription
      fasciaSize: $fasciaSize
      mechanism: $mechanism
      inventoryItemId: $inventoryItemId
      interliningType: $interliningType
      fabricRepeatInches: $fabricRepeatInches
      rodFaceInches: $rodFaceInches
      overlapInches: $overlapInches
      returnInches: $returnInches
      hemSize: $hemSize
      pleatType: $pleatType
      ruffle: $ruffle
      shamSize: $shamSize
      dropInches: $dropInches
      trimDescription: $trimDescription
      seatHeightInches: $seatHeightInches
      seatDepthInches: $seatDepthInches
      armHeightInches: $armHeightInches
      backHeightInches: $backHeightInches
      legHeightInches: $legHeightInches
      cushionThicknessInches: $cushionThicknessInches
      armThicknessInches: $armThicknessInches
      seatDeckThicknessInches: $seatDeckThicknessInches
      backCushionThicknessInches: $backCushionThicknessInches
      legProfile: $legProfile
      furnitureType: $furnitureType
      contrastWeltFabricDescription: $contrastWeltFabricDescription
      customWoodFascia: $customWoodFascia
      customWoodFasciaPaintColor: $customWoodFasciaPaintColor
      notes: $notes
    ) {
      objectId
    }
  }
`;

// Shirred Valance gets its own dedicated add/update mutations instead of the
// generic ADD_DRAFT_QUOTE_OBJECT_MUTATION/UPDATE_DRAFT_QUOTE_OBJECT_MUTATION
// above — a deliberate deviation from every other item type, requested so
// this type's create/update path has its own narrow, typed GraphQL surface.
// See createShirredValanceDraftQuoteObjectPlugin.ts / updateShirredValanceDraftQuoteObjectPlugin.ts.
export const ADD_SHIRRED_VALANCE_DRAFT_QUOTE_OBJECT_MUTATION = gql`
  mutation AddShirredValanceDraftQuoteObject(
    $draftQuoteId: Int!
    $name: String
    $sortOrder: Int!
    $quantity: Int
    $widthInches: Float
    $mountType: String
    $spaceAboveWindowInches: Float
    $fabricDescription: String
    $liningType: String
    $interliningType: String
    $trimPlacement: String
    $fabricWidthInches: Float
    $fabricRepeatInches: Float
    $inventoryItemId: Int
    $liningDescription: String
    $trimDescription: String
    $notes: String
  ) {
    addShirredValanceDraftQuoteObject(
      draftQuoteId: $draftQuoteId
      name: $name
      sortOrder: $sortOrder
      quantity: $quantity
      widthInches: $widthInches
      mountType: $mountType
      spaceAboveWindowInches: $spaceAboveWindowInches
      fabricDescription: $fabricDescription
      liningType: $liningType
      interliningType: $interliningType
      trimPlacement: $trimPlacement
      fabricWidthInches: $fabricWidthInches
      fabricRepeatInches: $fabricRepeatInches
      inventoryItemId: $inventoryItemId
      liningDescription: $liningDescription
      trimDescription: $trimDescription
      notes: $notes
    ) {
      objectId
    }
  }
`;

export const UPDATE_SHIRRED_VALANCE_DRAFT_QUOTE_OBJECT_MUTATION = gql`
  mutation UpdateShirredValanceDraftQuoteObject(
    $objectId: Int!
    $draftQuoteId: Int!
    $name: String
    $quantity: Int
    $widthInches: Float
    $mountType: String
    $spaceAboveWindowInches: Float
    $fabricDescription: String
    $liningType: String
    $interliningType: String
    $trimPlacement: String
    $fabricWidthInches: Float
    $fabricRepeatInches: Float
    $inventoryItemId: Int
    $liningDescription: String
    $trimDescription: String
    $notes: String
  ) {
    updateShirredValanceDraftQuoteObject(
      objectId: $objectId
      draftQuoteId: $draftQuoteId
      name: $name
      quantity: $quantity
      widthInches: $widthInches
      mountType: $mountType
      spaceAboveWindowInches: $spaceAboveWindowInches
      fabricDescription: $fabricDescription
      liningType: $liningType
      interliningType: $interliningType
      trimPlacement: $trimPlacement
      fabricWidthInches: $fabricWidthInches
      fabricRepeatInches: $fabricRepeatInches
      inventoryItemId: $inventoryItemId
      liningDescription: $liningDescription
      trimDescription: $trimDescription
      notes: $notes
    ) {
      objectId
    }
  }
`;

export const ADD_BOX_SPRING_COVER_DRAFT_QUOTE_OBJECT_MUTATION = gql`
  mutation AddBoxSpringCoverDraftQuoteObject(
    $draftQuoteId: Int!
    $name: String
    $sortOrder: Int!
    $quantity: Int
    $bedSize: String
    $widthInches: Float
    $heightInches: Float
    $depthInches: Float
    $fabricDescription: String
    $inventoryItemId: Int
    $liningDescription: String
    $notes: String
  ) {
    addBoxSpringCoverDraftQuoteObject(
      draftQuoteId: $draftQuoteId
      name: $name
      sortOrder: $sortOrder
      quantity: $quantity
      bedSize: $bedSize
      widthInches: $widthInches
      heightInches: $heightInches
      depthInches: $depthInches
      fabricDescription: $fabricDescription
      inventoryItemId: $inventoryItemId
      liningDescription: $liningDescription
      notes: $notes
    ) {
      objectId
    }
  }
`;

export const UPDATE_BOX_SPRING_COVER_DRAFT_QUOTE_OBJECT_MUTATION = gql`
  mutation UpdateBoxSpringCoverDraftQuoteObject(
    $objectId: Int!
    $draftQuoteId: Int!
    $name: String
    $quantity: Int
    $bedSize: String
    $widthInches: Float
    $heightInches: Float
    $depthInches: Float
    $fabricDescription: String
    $inventoryItemId: Int
    $liningDescription: String
    $notes: String
  ) {
    updateBoxSpringCoverDraftQuoteObject(
      objectId: $objectId
      draftQuoteId: $draftQuoteId
      name: $name
      quantity: $quantity
      bedSize: $bedSize
      widthInches: $widthInches
      heightInches: $heightInches
      depthInches: $depthInches
      fabricDescription: $fabricDescription
      inventoryItemId: $inventoryItemId
      liningDescription: $liningDescription
      notes: $notes
    ) {
      objectId
    }
  }
`;

export const ADD_BENCH_DRAFT_QUOTE_OBJECT_MUTATION = gql`
  mutation AddBenchDraftQuoteObject(
    $draftQuoteId: Int!
    $name: String
    $sortOrder: Int!
    $quantity: Int
    $benchType: String
    $benchShape: String
    $benchStyle: String
    $widthInches: Float
    $heightInches: Float
    $depthInches: Float
    $storageOption: Boolean
    $channelSize: String
    $pillowTopSize: String
    $fabricDescription: String
    $fabricWidthInches: Float
    $patternRepeatInches: Float
    $inventoryItemId: Int
    $legSize: String
    $legStyle: String
    $legFinish: String
    $legPriceCents: Int
    $trimTop: Boolean
    $trimBottom: Boolean
    $trimDescription: String
    $nailheadTop: Boolean
    $nailheadBottom: Boolean
    $nailheadColor: String
    $notes: String
  ) {
    addBenchDraftQuoteObject(
      draftQuoteId: $draftQuoteId
      name: $name
      sortOrder: $sortOrder
      quantity: $quantity
      benchType: $benchType
      benchShape: $benchShape
      benchStyle: $benchStyle
      widthInches: $widthInches
      heightInches: $heightInches
      depthInches: $depthInches
      storageOption: $storageOption
      channelSize: $channelSize
      pillowTopSize: $pillowTopSize
      fabricDescription: $fabricDescription
      fabricWidthInches: $fabricWidthInches
      patternRepeatInches: $patternRepeatInches
      inventoryItemId: $inventoryItemId
      legSize: $legSize
      legStyle: $legStyle
      legFinish: $legFinish
      legPriceCents: $legPriceCents
      trimTop: $trimTop
      trimBottom: $trimBottom
      trimDescription: $trimDescription
      nailheadTop: $nailheadTop
      nailheadBottom: $nailheadBottom
      nailheadColor: $nailheadColor
      notes: $notes
    ) {
      objectId
    }
  }
`;

export const UPDATE_BENCH_DRAFT_QUOTE_OBJECT_MUTATION = gql`
  mutation UpdateBenchDraftQuoteObject(
    $objectId: Int!
    $draftQuoteId: Int!
    $name: String
    $quantity: Int
    $benchType: String
    $benchShape: String
    $benchStyle: String
    $widthInches: Float
    $heightInches: Float
    $depthInches: Float
    $storageOption: Boolean
    $channelSize: String
    $pillowTopSize: String
    $fabricDescription: String
    $fabricWidthInches: Float
    $patternRepeatInches: Float
    $inventoryItemId: Int
    $legSize: String
    $legStyle: String
    $legFinish: String
    $legPriceCents: Int
    $trimTop: Boolean
    $trimBottom: Boolean
    $trimDescription: String
    $nailheadTop: Boolean
    $nailheadBottom: Boolean
    $nailheadColor: String
    $notes: String
  ) {
    updateBenchDraftQuoteObject(
      objectId: $objectId
      draftQuoteId: $draftQuoteId
      name: $name
      quantity: $quantity
      benchType: $benchType
      benchShape: $benchShape
      benchStyle: $benchStyle
      widthInches: $widthInches
      heightInches: $heightInches
      depthInches: $depthInches
      storageOption: $storageOption
      channelSize: $channelSize
      pillowTopSize: $pillowTopSize
      fabricDescription: $fabricDescription
      fabricWidthInches: $fabricWidthInches
      patternRepeatInches: $patternRepeatInches
      inventoryItemId: $inventoryItemId
      legSize: $legSize
      legStyle: $legStyle
      legFinish: $legFinish
      legPriceCents: $legPriceCents
      trimTop: $trimTop
      trimBottom: $trimBottom
      trimDescription: $trimDescription
      nailheadTop: $nailheadTop
      nailheadBottom: $nailheadBottom
      nailheadColor: $nailheadColor
      notes: $notes
    ) {
      objectId
    }
  }
`;

export const ADD_CORNICE_DRAFT_QUOTE_OBJECT_MUTATION = gql`
  mutation AddCorniceDraftQuoteObject(
    $draftQuoteId: Int!
    $sortOrder: Int!
    $name: String
    $quantity: Int
    $mountType: String
    $widthInches: Float
    $heightInches: Float
    $corniceShape: String
    $corniceOverProduct: Boolean
    $fabricDescription: String
    $fabricWidthInches: Float
    $patternRepeatInches: Float
    $inventoryItemId: Int
    $trimTop: Boolean
    $trimBottom: Boolean
    $trimSides: Boolean
    $trimFace: Boolean
    $trimDescription: String
    $weltTop: Boolean
    $weltBottom: Boolean
    $weltSides: Boolean
    $weltFace: Boolean
    $nailheadTop: Boolean
    $nailheadBottom: Boolean
    $nailheadSides: Boolean
    $nailheadFace: Boolean
    $nailheadColor: String
    $notes: String
  ) {
    addCorniceDraftQuoteObject(
      draftQuoteId: $draftQuoteId
      sortOrder: $sortOrder
      name: $name
      quantity: $quantity
      mountType: $mountType
      widthInches: $widthInches
      heightInches: $heightInches
      corniceShape: $corniceShape
      corniceOverProduct: $corniceOverProduct
      fabricDescription: $fabricDescription
      fabricWidthInches: $fabricWidthInches
      patternRepeatInches: $patternRepeatInches
      inventoryItemId: $inventoryItemId
      trimTop: $trimTop
      trimBottom: $trimBottom
      trimSides: $trimSides
      trimFace: $trimFace
      trimDescription: $trimDescription
      weltTop: $weltTop
      weltBottom: $weltBottom
      weltSides: $weltSides
      weltFace: $weltFace
      nailheadTop: $nailheadTop
      nailheadBottom: $nailheadBottom
      nailheadSides: $nailheadSides
      nailheadFace: $nailheadFace
      nailheadColor: $nailheadColor
      notes: $notes
    ) {
      objectId
    }
  }
`;

export const UPDATE_CORNICE_DRAFT_QUOTE_OBJECT_MUTATION = gql`
  mutation UpdateCorniceDraftQuoteObject(
    $objectId: Int!
    $draftQuoteId: Int!
    $name: String
    $quantity: Int
    $mountType: String
    $widthInches: Float
    $heightInches: Float
    $corniceShape: String
    $corniceOverProduct: Boolean
    $fabricDescription: String
    $fabricWidthInches: Float
    $patternRepeatInches: Float
    $inventoryItemId: Int
    $trimTop: Boolean
    $trimBottom: Boolean
    $trimSides: Boolean
    $trimFace: Boolean
    $trimDescription: String
    $weltTop: Boolean
    $weltBottom: Boolean
    $weltSides: Boolean
    $weltFace: Boolean
    $nailheadTop: Boolean
    $nailheadBottom: Boolean
    $nailheadSides: Boolean
    $nailheadFace: Boolean
    $nailheadColor: String
    $notes: String
  ) {
    updateCorniceDraftQuoteObject(
      objectId: $objectId
      draftQuoteId: $draftQuoteId
      name: $name
      quantity: $quantity
      mountType: $mountType
      widthInches: $widthInches
      heightInches: $heightInches
      corniceShape: $corniceShape
      corniceOverProduct: $corniceOverProduct
      fabricDescription: $fabricDescription
      fabricWidthInches: $fabricWidthInches
      patternRepeatInches: $patternRepeatInches
      inventoryItemId: $inventoryItemId
      trimTop: $trimTop
      trimBottom: $trimBottom
      trimSides: $trimSides
      trimFace: $trimFace
      trimDescription: $trimDescription
      weltTop: $weltTop
      weltBottom: $weltBottom
      weltSides: $weltSides
      weltFace: $weltFace
      nailheadTop: $nailheadTop
      nailheadBottom: $nailheadBottom
      nailheadSides: $nailheadSides
      nailheadFace: $nailheadFace
      nailheadColor: $nailheadColor
      notes: $notes
    ) {
      objectId
    }
  }
`;

export const ADD_BED_DRAFT_QUOTE_OBJECT_MUTATION = gql`
  mutation AddBedDraftQuoteObject(
    $draftQuoteId: Int!
    $name: String
    $sortOrder: Int!
    $quantity: Int
    $bedConfig: String
    $headboardStyle: String
    $headboardShaped: Boolean
    $widthInches: Float
    $heightInches: Float
    $fabricDescription: String
    $inventoryItemId: Int
    $fabricWidthInches: Float
    $patternRepeatInches: Float
    $bedEdgeTreatment: String
    $trimTop: Boolean
    $trimSides: Boolean
    $trimFace: Boolean
    $mattressLengthInches: Float
    $mattressWidthInches: Float
    $frameThicknessInches: Float
    $sideRailFootboardHeightInches: Float
    $sideRailFootboardStyleDescription: String
    $bedDrawersSmallQty: Int
    $bedDrawersLargeQty: Int
    $bedTrundle: Boolean
    $bedFeetType: String
    $bedFeetHeightInches: Float
    $bedFeetFinish: String
    $bedFeetSpecialtyDescription: String
    $bedFeetSpecialtyPriceCents: Int
    $bedBunkyBoards: Boolean
    $bedBunkyBoardWidthInches: Float
    $includeMeasureCharge: Boolean
    $notes: String
  ) {
    addBedDraftQuoteObject(
      draftQuoteId: $draftQuoteId
      name: $name
      sortOrder: $sortOrder
      quantity: $quantity
      bedConfig: $bedConfig
      headboardStyle: $headboardStyle
      headboardShaped: $headboardShaped
      widthInches: $widthInches
      heightInches: $heightInches
      fabricDescription: $fabricDescription
      inventoryItemId: $inventoryItemId
      fabricWidthInches: $fabricWidthInches
      patternRepeatInches: $patternRepeatInches
      bedEdgeTreatment: $bedEdgeTreatment
      trimTop: $trimTop
      trimSides: $trimSides
      trimFace: $trimFace
      mattressLengthInches: $mattressLengthInches
      mattressWidthInches: $mattressWidthInches
      frameThicknessInches: $frameThicknessInches
      sideRailFootboardHeightInches: $sideRailFootboardHeightInches
      sideRailFootboardStyleDescription: $sideRailFootboardStyleDescription
      bedDrawersSmallQty: $bedDrawersSmallQty
      bedDrawersLargeQty: $bedDrawersLargeQty
      bedTrundle: $bedTrundle
      bedFeetType: $bedFeetType
      bedFeetHeightInches: $bedFeetHeightInches
      bedFeetFinish: $bedFeetFinish
      bedFeetSpecialtyDescription: $bedFeetSpecialtyDescription
      bedFeetSpecialtyPriceCents: $bedFeetSpecialtyPriceCents
      bedBunkyBoards: $bedBunkyBoards
      bedBunkyBoardWidthInches: $bedBunkyBoardWidthInches
      includeMeasureCharge: $includeMeasureCharge
      notes: $notes
    ) {
      objectId
    }
  }
`;

export const UPDATE_BED_DRAFT_QUOTE_OBJECT_MUTATION = gql`
  mutation UpdateBedDraftQuoteObject(
    $objectId: Int!
    $draftQuoteId: Int!
    $name: String
    $quantity: Int
    $bedConfig: String
    $headboardStyle: String
    $headboardShaped: Boolean
    $widthInches: Float
    $heightInches: Float
    $fabricDescription: String
    $inventoryItemId: Int
    $fabricWidthInches: Float
    $patternRepeatInches: Float
    $bedEdgeTreatment: String
    $trimTop: Boolean
    $trimSides: Boolean
    $trimFace: Boolean
    $mattressLengthInches: Float
    $mattressWidthInches: Float
    $frameThicknessInches: Float
    $sideRailFootboardHeightInches: Float
    $sideRailFootboardStyleDescription: String
    $bedDrawersSmallQty: Int
    $bedDrawersLargeQty: Int
    $bedTrundle: Boolean
    $bedFeetType: String
    $bedFeetHeightInches: Float
    $bedFeetFinish: String
    $bedFeetSpecialtyDescription: String
    $bedFeetSpecialtyPriceCents: Int
    $bedBunkyBoards: Boolean
    $bedBunkyBoardWidthInches: Float
    $includeMeasureCharge: Boolean
    $notes: String
  ) {
    updateBedDraftQuoteObject(
      objectId: $objectId
      draftQuoteId: $draftQuoteId
      name: $name
      quantity: $quantity
      bedConfig: $bedConfig
      headboardStyle: $headboardStyle
      headboardShaped: $headboardShaped
      widthInches: $widthInches
      heightInches: $heightInches
      fabricDescription: $fabricDescription
      inventoryItemId: $inventoryItemId
      fabricWidthInches: $fabricWidthInches
      patternRepeatInches: $patternRepeatInches
      bedEdgeTreatment: $bedEdgeTreatment
      trimTop: $trimTop
      trimSides: $trimSides
      trimFace: $trimFace
      mattressLengthInches: $mattressLengthInches
      mattressWidthInches: $mattressWidthInches
      frameThicknessInches: $frameThicknessInches
      sideRailFootboardHeightInches: $sideRailFootboardHeightInches
      sideRailFootboardStyleDescription: $sideRailFootboardStyleDescription
      bedDrawersSmallQty: $bedDrawersSmallQty
      bedDrawersLargeQty: $bedDrawersLargeQty
      bedTrundle: $bedTrundle
      bedFeetType: $bedFeetType
      bedFeetHeightInches: $bedFeetHeightInches
      bedFeetFinish: $bedFeetFinish
      bedFeetSpecialtyDescription: $bedFeetSpecialtyDescription
      bedFeetSpecialtyPriceCents: $bedFeetSpecialtyPriceCents
      bedBunkyBoards: $bedBunkyBoards
      bedBunkyBoardWidthInches: $bedBunkyBoardWidthInches
      includeMeasureCharge: $includeMeasureCharge
      notes: $notes
    ) {
      objectId
    }
  }
`;

export const ADD_AUSTRIAN_SHADE_DRAFT_QUOTE_OBJECT_MUTATION = gql`
  mutation AddAustrianShadeDraftQuoteObject(
    $draftQuoteId: Int!
    $name: String
    $sortOrder: Int!
    $quantity: Int
    $widthInches: Float
    $heightInches: Float
    $mountType: String
    $spaceAboveWindowInches: Float
    $fabricWidthInches: Float
    $patternRepeatInches: Float
    $fabricDescription: String
    $inventoryItemId: Int
    $liningType: String
    $liningDescription: String
    $bumpInterlining: Boolean
    $trimPlacement: String
    $operatingSystem: String
    $trimDescription: String
    $notes: String
  ) {
    addAustrianShadeDraftQuoteObject(
      draftQuoteId: $draftQuoteId
      name: $name
      sortOrder: $sortOrder
      quantity: $quantity
      widthInches: $widthInches
      heightInches: $heightInches
      mountType: $mountType
      spaceAboveWindowInches: $spaceAboveWindowInches
      fabricWidthInches: $fabricWidthInches
      patternRepeatInches: $patternRepeatInches
      fabricDescription: $fabricDescription
      inventoryItemId: $inventoryItemId
      liningType: $liningType
      liningDescription: $liningDescription
      bumpInterlining: $bumpInterlining
      trimPlacement: $trimPlacement
      operatingSystem: $operatingSystem
      trimDescription: $trimDescription
      notes: $notes
    ) {
      objectId
    }
  }
`;

export const UPDATE_AUSTRIAN_SHADE_DRAFT_QUOTE_OBJECT_MUTATION = gql`
  mutation UpdateAustrianShadeDraftQuoteObject(
    $objectId: Int!
    $draftQuoteId: Int!
    $name: String
    $quantity: Int
    $widthInches: Float
    $heightInches: Float
    $mountType: String
    $spaceAboveWindowInches: Float
    $fabricWidthInches: Float
    $patternRepeatInches: Float
    $fabricDescription: String
    $inventoryItemId: Int
    $liningType: String
    $liningDescription: String
    $bumpInterlining: Boolean
    $trimPlacement: String
    $operatingSystem: String
    $trimDescription: String
    $notes: String
  ) {
    updateAustrianShadeDraftQuoteObject(
      objectId: $objectId
      draftQuoteId: $draftQuoteId
      name: $name
      quantity: $quantity
      widthInches: $widthInches
      heightInches: $heightInches
      mountType: $mountType
      spaceAboveWindowInches: $spaceAboveWindowInches
      fabricWidthInches: $fabricWidthInches
      patternRepeatInches: $patternRepeatInches
      fabricDescription: $fabricDescription
      inventoryItemId: $inventoryItemId
      liningType: $liningType
      liningDescription: $liningDescription
      bumpInterlining: $bumpInterlining
      trimPlacement: $trimPlacement
      operatingSystem: $operatingSystem
      trimDescription: $trimDescription
      notes: $notes
    ) {
      objectId
    }
  }
`;

export const ADD_BALLOON_SHADE_DRAFT_QUOTE_OBJECT_MUTATION = gql`
  mutation AddBalloonShadeDraftQuoteObject(
    $draftQuoteId: Int!
    $name: String
    $sortOrder: Int!
    $quantity: Int
    $widthInches: Float
    $heightInches: Float
    $mountType: String
    $spaceAboveWindowInches: Float
    $fabricWidthInches: Float
    $patternRepeatInches: Float
    $fabricDescription: String
    $inventoryItemId: Int
    $liningType: String
    $liningDescription: String
    $bumpInterlining: Boolean
    $trimPlacement: String
    $operatingSystem: String
    $trimDescription: String
    $notes: String
  ) {
    addBalloonShadeDraftQuoteObject(
      draftQuoteId: $draftQuoteId
      name: $name
      sortOrder: $sortOrder
      quantity: $quantity
      widthInches: $widthInches
      heightInches: $heightInches
      mountType: $mountType
      spaceAboveWindowInches: $spaceAboveWindowInches
      fabricWidthInches: $fabricWidthInches
      patternRepeatInches: $patternRepeatInches
      fabricDescription: $fabricDescription
      inventoryItemId: $inventoryItemId
      liningType: $liningType
      liningDescription: $liningDescription
      bumpInterlining: $bumpInterlining
      trimPlacement: $trimPlacement
      operatingSystem: $operatingSystem
      trimDescription: $trimDescription
      notes: $notes
    ) {
      objectId
    }
  }
`;

export const UPDATE_BALLOON_SHADE_DRAFT_QUOTE_OBJECT_MUTATION = gql`
  mutation UpdateBalloonShadeDraftQuoteObject(
    $objectId: Int!
    $draftQuoteId: Int!
    $name: String
    $quantity: Int
    $widthInches: Float
    $heightInches: Float
    $mountType: String
    $spaceAboveWindowInches: Float
    $fabricWidthInches: Float
    $patternRepeatInches: Float
    $fabricDescription: String
    $inventoryItemId: Int
    $liningType: String
    $liningDescription: String
    $bumpInterlining: Boolean
    $trimPlacement: String
    $operatingSystem: String
    $trimDescription: String
    $notes: String
  ) {
    updateBalloonShadeDraftQuoteObject(
      objectId: $objectId
      draftQuoteId: $draftQuoteId
      name: $name
      quantity: $quantity
      widthInches: $widthInches
      heightInches: $heightInches
      mountType: $mountType
      spaceAboveWindowInches: $spaceAboveWindowInches
      fabricWidthInches: $fabricWidthInches
      patternRepeatInches: $patternRepeatInches
      fabricDescription: $fabricDescription
      inventoryItemId: $inventoryItemId
      liningType: $liningType
      liningDescription: $liningDescription
      bumpInterlining: $bumpInterlining
      trimPlacement: $trimPlacement
      operatingSystem: $operatingSystem
      trimDescription: $trimDescription
      notes: $notes
    ) {
      objectId
    }
  }
`;

export const ADD_DUVET_DRAFT_QUOTE_OBJECT_MUTATION = gql`
  mutation AddDuvetDraftQuoteObject(
    $draftQuoteId: Int!
    $name: String
    $sortOrder: Int!
    $quantity: Int
    $bedSize: String
    $weltType: String
    $duvetInsert: Boolean
    $fabricDescription: String
    $inventoryItemId: Int
    $liningDescription: String
    $notes: String
  ) {
    addDuvetDraftQuoteObject(
      draftQuoteId: $draftQuoteId
      name: $name
      sortOrder: $sortOrder
      quantity: $quantity
      bedSize: $bedSize
      weltType: $weltType
      duvetInsert: $duvetInsert
      fabricDescription: $fabricDescription
      inventoryItemId: $inventoryItemId
      liningDescription: $liningDescription
      notes: $notes
    ) {
      objectId
    }
  }
`;

export const UPDATE_DUVET_DRAFT_QUOTE_OBJECT_MUTATION = gql`
  mutation UpdateDuvetDraftQuoteObject(
    $objectId: Int!
    $draftQuoteId: Int!
    $name: String
    $quantity: Int
    $bedSize: String
    $weltType: String
    $duvetInsert: Boolean
    $fabricDescription: String
    $inventoryItemId: Int
    $liningDescription: String
    $notes: String
  ) {
    updateDuvetDraftQuoteObject(
      objectId: $objectId
      draftQuoteId: $draftQuoteId
      name: $name
      quantity: $quantity
      bedSize: $bedSize
      weltType: $weltType
      duvetInsert: $duvetInsert
      fabricDescription: $fabricDescription
      inventoryItemId: $inventoryItemId
      liningDescription: $liningDescription
      notes: $notes
    ) {
      objectId
    }
  }
`;

export const ADD_COVERLET_DRAFT_QUOTE_OBJECT_MUTATION = gql`
  mutation AddCoverletDraftQuoteObject(
    $draftQuoteId: Int!
    $name: String
    $sortOrder: Int!
    $quantity: Int
    $bedSize: String
    $fabricDescription: String
    $inventoryItemId: Int
    $liningDescription: String
    $notes: String
  ) {
    addCoverletDraftQuoteObject(
      draftQuoteId: $draftQuoteId
      name: $name
      sortOrder: $sortOrder
      quantity: $quantity
      bedSize: $bedSize
      fabricDescription: $fabricDescription
      inventoryItemId: $inventoryItemId
      liningDescription: $liningDescription
      notes: $notes
    ) {
      objectId
    }
  }
`;

export const UPDATE_COVERLET_DRAFT_QUOTE_OBJECT_MUTATION = gql`
  mutation UpdateCoverletDraftQuoteObject(
    $objectId: Int!
    $draftQuoteId: Int!
    $name: String
    $quantity: Int
    $bedSize: String
    $fabricDescription: String
    $inventoryItemId: Int
    $liningDescription: String
    $notes: String
  ) {
    updateCoverletDraftQuoteObject(
      objectId: $objectId
      draftQuoteId: $draftQuoteId
      name: $name
      quantity: $quantity
      bedSize: $bedSize
      fabricDescription: $fabricDescription
      inventoryItemId: $inventoryItemId
      liningDescription: $liningDescription
      notes: $notes
    ) {
      objectId
    }
  }
`;

export const ADD_TABLECLOTH_ROUND_DRAFT_QUOTE_OBJECT_MUTATION = gql`
  mutation AddTableclothRoundDraftQuoteObject(
    $draftQuoteId: Int!
    $name: String
    $sortOrder: Int!
    $quantity: Int
    $widthInches: Float
    $heightInches: Float
    $fabricWidthInches: Float
    $patternRepeatInches: Float
    $fabricDescription: String
    $inventoryItemId: Int
    $liningDescription: String
    $notes: String
  ) {
    addTableclothRoundDraftQuoteObject(
      draftQuoteId: $draftQuoteId
      name: $name
      sortOrder: $sortOrder
      quantity: $quantity
      widthInches: $widthInches
      heightInches: $heightInches
      fabricWidthInches: $fabricWidthInches
      patternRepeatInches: $patternRepeatInches
      fabricDescription: $fabricDescription
      inventoryItemId: $inventoryItemId
      liningDescription: $liningDescription
      notes: $notes
    ) {
      objectId
    }
  }
`;

export const UPDATE_TABLECLOTH_ROUND_DRAFT_QUOTE_OBJECT_MUTATION = gql`
  mutation UpdateTableclothRoundDraftQuoteObject(
    $objectId: Int!
    $draftQuoteId: Int!
    $name: String
    $quantity: Int
    $widthInches: Float
    $heightInches: Float
    $fabricWidthInches: Float
    $patternRepeatInches: Float
    $fabricDescription: String
    $inventoryItemId: Int
    $liningDescription: String
    $notes: String
  ) {
    updateTableclothRoundDraftQuoteObject(
      objectId: $objectId
      draftQuoteId: $draftQuoteId
      name: $name
      quantity: $quantity
      widthInches: $widthInches
      heightInches: $heightInches
      fabricWidthInches: $fabricWidthInches
      patternRepeatInches: $patternRepeatInches
      fabricDescription: $fabricDescription
      inventoryItemId: $inventoryItemId
      liningDescription: $liningDescription
      notes: $notes
    ) {
      objectId
    }
  }
`;

export const ADD_TABLECLOTH_RECTANGLE_DRAFT_QUOTE_OBJECT_MUTATION = gql`
  mutation AddTableclothRectangleDraftQuoteObject(
    $draftQuoteId: Int!
    $name: String
    $sortOrder: Int!
    $quantity: Int
    $widthInches: Float
    $heightInches: Float
    $depthInches: Float
    $fabricWidthInches: Float
    $patternRepeatInches: Float
    $fabricDescription: String
    $inventoryItemId: Int
    $liningDescription: String
    $notes: String
  ) {
    addTableclothRectangleDraftQuoteObject(
      draftQuoteId: $draftQuoteId
      name: $name
      sortOrder: $sortOrder
      quantity: $quantity
      widthInches: $widthInches
      heightInches: $heightInches
      depthInches: $depthInches
      fabricWidthInches: $fabricWidthInches
      patternRepeatInches: $patternRepeatInches
      fabricDescription: $fabricDescription
      inventoryItemId: $inventoryItemId
      liningDescription: $liningDescription
      notes: $notes
    ) {
      objectId
    }
  }
`;

export const UPDATE_TABLECLOTH_RECTANGLE_DRAFT_QUOTE_OBJECT_MUTATION = gql`
  mutation UpdateTableclothRectangleDraftQuoteObject(
    $objectId: Int!
    $draftQuoteId: Int!
    $name: String
    $quantity: Int
    $widthInches: Float
    $heightInches: Float
    $depthInches: Float
    $fabricWidthInches: Float
    $patternRepeatInches: Float
    $fabricDescription: String
    $inventoryItemId: Int
    $liningDescription: String
    $notes: String
  ) {
    updateTableclothRectangleDraftQuoteObject(
      objectId: $objectId
      draftQuoteId: $draftQuoteId
      name: $name
      quantity: $quantity
      widthInches: $widthInches
      heightInches: $heightInches
      depthInches: $depthInches
      fabricWidthInches: $fabricWidthInches
      patternRepeatInches: $patternRepeatInches
      fabricDescription: $fabricDescription
      inventoryItemId: $inventoryItemId
      liningDescription: $liningDescription
      notes: $notes
    ) {
      objectId
    }
  }
`;

// For manually adding a custom line item (fabric swatch, special hardware, etc.)
// to an existing object. Uses PostGraphile's auto-generated mutation.
export const CREATE_DRAFT_QUOTE_OBJECT_ITEM_MUTATION = gql`
  mutation CreateDraftQuoteObjectItem(
    $draftQuoteId: Int!
    $draftQuoteObjectId: Int!
    $section: String!
    $description: String!
    $quantity: BigFloat!
    $unitPrice: BigInt!
    $notes: String
  ) {
    createDraftQuoteObjectItem(
      input: {
        draftQuoteObjectItem: {
          draftQuoteId: $draftQuoteId
          draftQuoteObjectId: $draftQuoteObjectId
          section: $section
          description: $description
          quantity: $quantity
          unitPrice: $unitPrice
          notes: $notes
        }
      }
    ) {
      draftQuoteObjectItem {
        id
      }
    }
  }
`;

export const UPDATE_DRAFT_QUOTE_OBJECT_ITEM_MUTATION = gql`
  mutation UpdateDraftQuoteObjectItem($id: Int!, $quantity: BigFloat!, $description: String!, $unitPrice: BigInt!) {
    updateDraftQuoteObjectItemById(input: {
      id: $id
      draftQuoteObjectItemPatch: { quantity: $quantity, description: $description, unitPrice: $unitPrice }
    }) {
      draftQuoteObjectItem { id }
    }
  }
`;

// Issue #51: lets the shop correct a line item's QuickBooks product_type by
// hand (e.g. a mis-tagged legacy row) without touching its price/qty/desc —
// a separate mutation from UPDATE_DRAFT_QUOTE_OBJECT_ITEM_MUTATION above so
// existing quantity/description/price edit call sites are untouched.
export const UPDATE_DRAFT_QUOTE_OBJECT_ITEM_PRODUCT_TYPE_MUTATION = gql`
  mutation UpdateDraftQuoteObjectItemProductType($id: Int!, $productType: ProductTypeEnum) {
    updateDraftQuoteObjectItemById(input: {
      id: $id
      draftQuoteObjectItemPatch: { productType: $productType }
    }) {
      draftQuoteObjectItem { id }
    }
  }
`;

// $archivedAt is nullable so this mutation can also un-archive (pass null) —
// used by the "Go back" edit flow to restore an object if its edit is
// abandoned instead of saved.
export const ARCHIVE_DRAFT_QUOTE_OBJECT_MUTATION = gql`
  mutation ArchiveDraftQuoteObject($id: Int!, $archivedAt: Datetime) {
    updateDraftQuoteObjectById(input: {
      id: $id
      draftQuoteObjectPatch: { archivedAt: $archivedAt }
    }) {
      draftQuoteObject { id }
    }
  }
`;

export const DELETE_DRAFT_QUOTE_OBJECT_MUTATION = gql`
  mutation DeleteDraftQuoteObject($id: Int!) {
    deleteDraftQuoteObjectById(input: { id: $id }) {
      deletedDraftQuoteObjectId
    }
  }
`;

export const ARCHIVE_DRAFT_QUOTE_OBJECT_IMAGE_MUTATION = gql`
  mutation ArchiveDraftQuoteObjectImage($id: Int!, $archivedAt: Datetime!) {
    updateDraftQuoteObjectImageById(input: {
      id: $id
      draftQuoteObjectImagePatch: { archivedAt: $archivedAt }
    }) {
      draftQuoteObjectImage { id }
    }
  }
`;

export const FINALIZE_QUOTE_MUTATION = gql`
  mutation FinalizeQuote($draftQuoteId: Int!) {
    finalizeQuote(draftQuoteId: $draftQuoteId) {
      quoteId
    }
  }
`;

export const RESYNC_FINALIZED_QUOTE_MUTATION = gql`
  mutation ResyncFinalizedQuote($draftQuoteId: Int!) {
    resyncFinalizedQuote(draftQuoteId: $draftQuoteId) {
      quoteId
      lineItemCount
    }
  }
`;
